/**
 * 推荐产品购物车处理工具
 * 用于处理与推荐产品相关的购物车操作
 */

class RecommendedProductsCart {
  constructor() {
    this.selectedProducts = [];
    this.init();
  }

  init() {
    // 监听选中推荐产品的事件
    document.addEventListener('recommendedProductsSelected', this.handleProductsSelected.bind(this));
    
    // 监听加入购物车按钮点击事件
    document.addEventListener('cart:add-multiple', this.handleAddMultipleToCart.bind(this));
  }

  /**
   * 处理选中推荐产品的事件
   * @param {CustomEvent} event - 包含选中产品信息的事件对象
   */
  handleProductsSelected(event) {
    this.selectedProducts = event.detail.selectedProducts || [];
    
    // 更新价格显示
    this.updateTotalPrice();
    
    // 触发自定义事件，通知其他组件推荐产品已更新
    const updateEvent = new CustomEvent('recommendedProducts:updated', {
      detail: {
        selectedProducts: this.selectedProducts,
        totalPrice: this.calculateTotalPrice()
      },
      bubbles: true
    });
    
    document.dispatchEvent(updateEvent);
  }

  /**
   * 计算选中推荐产品的总价
   * @returns {number} 总价（以分为单位）
   */
  calculateTotalPrice() {
    let totalPrice = 0;
    
    this.selectedProducts.forEach(product => {
      const priceInfo = this.getProductPriceInfo(product.variantId);
      if (priceInfo && priceInfo.price) {
        totalPrice += priceInfo.price * product.quantity;
      }
    });
    
    return totalPrice;
  }

  /**
   * 获取产品的价格信息
   * @param {string|number} variantId - 产品变体ID
   * @returns {Object|null} 价格信息对象，如果未找到则返回null
   */
  getProductPriceInfo(variantId) {
    const recommendedSelector = document.querySelector('recommended-products-selector');
    if (recommendedSelector && recommendedSelector.currentPrices) {
      return recommendedSelector.currentPrices.get(variantId);
    }
    return null;
  }

  /**
   * 更新价格容器中的总价显示
   */
  updateTotalPrice() {
    const priceContainer = document.querySelector('.price-container');
    if (!priceContainer) return;
    
    // 如果有选中的推荐产品，更新主产品价格显示
    if (this.selectedProducts.length > 0) {
      const totalPrice = this.calculateTotalPrice();
      
      // 分发价格更新事件
      const priceUpdateEvent = new CustomEvent('recommendedProducts:priceUpdate', {
        detail: {
          additionalPrice: totalPrice,
          selectedProducts: this.selectedProducts
        },
        bubbles: true
      });
      
      document.dispatchEvent(priceUpdateEvent);
    }
  }

  /**
   * 处理多个产品添加到购物车的事件
   * @param {CustomEvent} event - 包含购物车添加信息的事件对象
   */
  handleAddMultipleToCart(event) {
    // 阻止默认行为
    event.preventDefault();
    
    // 获取主产品信息
    const mainProductForm =
      event.detail?.form ||
      event.detail?.button?.closest('form[data-type="add-to-cart-form"]');
    if (!mainProductForm) return;
    
    // 获取触发事件的按钮
    const triggerButton = event.detail.button || event.target;
    
    // 设置loading状态
    this.startLoading(triggerButton);
    
    const formData = new FormData(mainProductForm);
    const mainVariantId = formData.get('id');
    const mainQuantity = formData.get('quantity') || 1;
    
    // 准备加入购物车的项目 - 先添加主产品
    const items = [
      {
        id: mainVariantId,
        quantity: mainQuantity
      }
    ];
    
    // 处理事件数据
    const eventDetail = event.detail;
    
    // 兼容旧的数据结构（推荐产品） 可以删除
    if (eventDetail.selectedProducts && eventDetail.selectedProducts.length > 0) {
      eventDetail.selectedProducts.forEach(product => {
        items.push({
          id: product.variantId || product.id,
          quantity: product.quantity || 1
        });
      });
    }
    
    // 处理新的数据结构（包括特惠配送）
    if (eventDetail.productsToAdd && eventDetail.productsToAdd.length > 0) {
      eventDetail.productsToAdd.forEach(product => {
        const item = {
          id: product.id || product.variantId,
          quantity: product.quantity || 1
        };
        
        // 如果有properties，添加到item中
        if (product.properties) {
          item.properties = product.properties;
        }
        
        items.push(item);
      });
    }
    
    // 收集所有cart attributes
    const attributes = {};
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('attributes[')) {
        const attributeKey = key.match(/attributes\[(.*)\]/)[1];
        attributes[attributeKey] = value;
      }
    }
    
    // 添加到购物车
    this.addItemsToCart(items, triggerButton, attributes, eventDetail.hasSpecialDelivery);
  }

  /**
   * 将多个商品添加到购物车
   * @param {Array} items - 要添加的商品数组
   * @param {HTMLElement} triggerButton - 触发加购的按钮
   * @param {Object} attributes - 购物车属性
   * @param {boolean} hasSpecialDelivery - 是否包含特惠配送
   */
  addItemsToCart(items, triggerButton, attributes = {}, hasSpecialDelivery = false) {
    // 获取购物车抽屉元素
    const cartDrawer = document.getElementById('Cart-Drawer');
    
    // 获取需要更新的sections
    const sectionsToRender = this.getSectionsToRender();
    const sectionsIds = sectionsToRender.map((section) => section.section).join(',');
    
    // 构建请求体
    const requestBody = {
      items: items,
      sections: sectionsIds,
      sections_url: window.location.pathname
    };
    
    // 如果有属性，添加到请求体中
    if (attributes && Object.keys(attributes).length > 0) {
      requestBody.attributes = attributes;
    }
    
    const body = JSON.stringify(requestBody);
    
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('添加到购物车失败');
      }
      return response.json();
    })
    .then(response => {
      // 处理成功响应
      if (response.status && response.status !== 200) {
        console.error('添加商品到购物车时出错:', response.description);
        if (typeof popToast === 'function') {
          popToast(response.description || '添加商品到购物车时出错，请重试。', 'error');
        }
        return;
      }
      
      // 动态更新Sections内容
      if (window.SectionDynamicUpdate && window.SectionDynamicUpdate.updateSections && response.sections) {
        window.SectionDynamicUpdate.updateSections(sectionsToRender, response.sections);
      }
      
      // 触发购物车更新事件
      if (window.PUB_SUB_EVENTS && window.publish) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'recommended-products-cart',
          cartData: response
        });
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));
      }
      
      // 使用购物车抽屉的原生方法打开抽屉
      if (cartDrawer && typeof cartDrawer.show === 'function' && !cartDrawer.hasAttribute('data-status-silence')) {
        cartDrawer.show(triggerButton);
      }
      
      // 如果包含特惠配送，触发特惠配送添加成功事件
      if (hasSpecialDelivery) {
        document.dispatchEvent(new CustomEvent('special-delivery:added', {
          detail: {
            cartData: response,
            items: items.filter(item => item.properties && item.properties._is_special_delivery)
          },
          bubbles: true
        }));
      }
    })
    .catch(error => {
      console.error('添加商品到购物车时出错:', error);
      // 显示错误消息
      if (typeof popToast === 'function') {
        popToast('添加商品到购物车时出错，请重试。', 'error');
      }
    })
    .finally(() => {
      this.endLoading(triggerButton);
    });
  }
  
  /**
   * 获取需要重新渲染的位置
   * @returns {Array} 需要重新渲染的sections
   */
  getSectionsToRender() {
    const sections = [
      {
        id: 'Cart-Icon-Bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
    
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer && !cartDrawer.hasAttribute('data-status-silence')) {
      sections.push({
        id: 'Cart-Drawer',
        section: cartDrawer.dataset.section,
        selector: '#Cart-Drawer-Details',
      });
    }
    
    return sections;
  }
  
  /**
   * 开始加载状态
   * @param {HTMLElement} button - 触发按钮
   */
  startLoading(button) {
    this.loadingButtons = [];

    const form = button instanceof HTMLElement ? button.closest('form') : null;
    const addCartButtons = form ? form.querySelectorAll('button[type="submit"]') : [];

    addCartButtons.forEach(btn => {
      if (btn.hasAttribute('disabled')) return;
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('loading');
      this.loadingButtons.push(btn);
    });
  }
  
  /**
   * 结束加载状态
   * @param {HTMLElement} button - 触发按钮
   */
  endLoading(button) {
    (this.loadingButtons || []).forEach(btn => {
      btn.removeAttribute('disabled');
      btn.classList.remove('loading');
    });
    this.loadingButtons = [];

    if (button instanceof HTMLElement) {
      button.removeAttribute('disabled');
      button.classList.remove('loading');
    }
  }
}

// 实例化并初始化
if (typeof window !== 'undefined') {
  window.recommendedProductsCart = new RecommendedProductsCart();
  
  // 确保SectionDynamicUpdate对象存在
  if (!window.SectionDynamicUpdate) {
    window.SectionDynamicUpdate = {
      updateSections: function(sections, sectionsData) {
        if (!sections || !sectionsData) return;
        
        sections.forEach(section => {
          const elementToUpdate = document.getElementById(section.id);
          if (elementToUpdate && sectionsData[section.section]) {
            const newContent = new DOMParser()
              .parseFromString(sectionsData[section.section], 'text/html')
              .querySelector(section.selector);
            
            if (newContent) {
              if (section.selector === '.shopify-section') {
                elementToUpdate.innerHTML = newContent.innerHTML;
              } else {
                const targetElement = elementToUpdate.querySelector(section.selector);
                if (targetElement) {
                  targetElement.innerHTML = newContent.innerHTML;
                }
              }
            }
          }
        });
      }
    };
  }
} 